import { WorkbenchMenuSchema } from '@tencent/workbench';
import {
    COMMON_INSERT_LIST,
    COMMON_MENU_SCHEMA,
    DIVIDER,
    MenuKey,
} from '@framework/workbench/schema/menu-schema/common';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { wrapIdForBig, wrapIdForCustomContent, wrapIdForVip } from '@framework/workbench/util/wrap-id-util';
import appConfigInstance, { setDefaultConfig } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';
import { FileMenuConfig, ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { vipMenuConfig } from '@src/framework/workbench/ui/single-menu/config';
import { FileMenuKey } from '@tencent/docs-titlebar-service';

// eslint-disable-next-line max-len
const usePrivateAuthMenu = setDefaultConfig(appConfigInstance.getConfig(ToolbarFileMenuConfig.usePrivateAuthMenu as any), false);
const exitDocument = setDefaultConfig(appConfigInstance.getConfig(ToolbarFileMenuConfig.exitDocument as any), false);
const horMenu = setDefaultConfig(appConfigInstance.getConfig(FileMenuConfig.horMenu as any), true);
const showAccessibility = setDefaultConfig(appConfigInstance.getConfig(FileMenuConfig.showAccessibility as any), false);

// 格式转换下拉
const PDF_TRANSFORM_MENU = {
    key: ActionType.PDF_TRANSFORM,
    items: [
        `${ActionType.EXPORT_PDF}-toolbar`,
        DIVIDER,
        ActionType.PDF_TO_WORD,
        ActionType.PDF_TO_EXCEL,
        ActionType.PDF_TO_PPT,
    ],
};

const REPLACE_PICTURE_MENU = {
    key: ActionType.REPLACE_PICTURE,
    items: [
        ActionType.REPLACE_PICTURE_FROM_FILE,
        ActionType.REPLACE_PICTURE_FROM_CLIPBOARD,
    ],
};

const RESET_PICTURE_MENU = {
    key: ActionType.RESET_PICTURE,
    items: [
        ActionType.RESET_PICTURE_SELF,
        ActionType.RESET_PICTURE_SIZE,
    ],
};

const CROP_TO_SHAPE = {
    key: ActionType.CROP_TO_SHAPE,
    items: [
        ActionType.TAILOR_PIC_SHAPE,
    ],
};

const ROTATE = {
    key: ActionType.ROTATE,
    items: [
        ActionType.ROTATE_RIGHT_90,
        ActionType.ROTATE_LEFT_90,
        ActionType.FLIP_VERTICAL,
        ActionType.FLIP_HORIZONTAL,
    ],
};

// 插入下拉
export const INSERT_LIST = [
    ...COMMON_INSERT_LIST,
    DIVIDER,
    ActionType.INSERT_COMMENT,
    DIVIDER,
    ActionType.INSERT_TENCENT_DOC_ID,
    ActionType.UPLOAD_LOCAL_FILE,
    {
        key: ActionType.INSERT_IFRAME,
        items: [
        ],
    },
];

// 插件下拉
const INTELLGENT_TOOL_LIST = [
    ActionType.ADDON_MALL_SETTING,
    ActionType.ADDON_MALL_SHOP,
];

// 单栏-菜单下拉
const SINGLE_TOOLBAR_MENU_LIST = [
    {
        key: `${ActionType.INSERT_ALL}-menu-parent`,
        items: [
            ActionType.INSERT_BLANK_SLIDE,
            {
                key: ActionType.INSERT_TEMPLATE_SLIDE,
                items: [
                    `${ActionType.INSERT_TEMPLATE_SLIDE}-drop-content`,
                ],
            },
            ...INSERT_LIST,
        ],
    },
    ActionType.INSERT_ANIMATION,
    {
        key: 'toolbar-efficiency-parent',
        items: [
            {
                key: 'toolbar-picture-short-cut',
                items: [
                    ActionType.MUTI_PICTURE_SIZE_SETTING,
                    ActionType.MUTI_PICTURE_POS_SETTING,
                    ActionType.MUTI_PICTURE_EXPORT,
                ],
            },
            {
                key: 'toolbar-font-short-cut',
                items: [
                    ActionType.UNIFORM_FONT,
                ],
            },
            PDF_TRANSFORM_MENU,
            ActionType.EXPORT_IMAGE,
            {
                key: ActionType.INTELLIGENT_TOOL,
                items: INTELLGENT_TOOL_LIST,
            },
        ],
    },
    {
        key: 'play-menu-parent',
        items: [
            ActionType.LOCAL_PLAY_FROM_BEGIN,
            ActionType.LOCAL_PLAY_FROM_CURRENT,
            ActionType.LOCAL_WITH_PRESENTER_VIEW,
            ActionType.MOBILE_REMOTE_PLAY,
            ActionType.REMOTE_PLAY,
            ActionType.TENCENT_MEETING_PLAY,
        ],
    },
    {
        key: vipMenuConfig.name,
        items: [
            {
                key: wrapIdForVip(ActionType.INSERT_TEMPLATE_SLIDE),
                items: [
                    `${ActionType.INSERT_TEMPLATE_SLIDE}-drop-content`,
                ],
            },
            {
                key: 'toolbar-picture-short-cut',
                items: [
                    ActionType.MUTI_PICTURE_SIZE_SETTING,
                    ActionType.MUTI_PICTURE_POS_SETTING,
                    ActionType.MUTI_PICTURE_EXPORT,
                ],
            },
            {
                key: 'toolbar-font-short-cut',
                items: [
                    ActionType.UNIFORM_FONT,
                ],
            },
            PDF_TRANSFORM_MENU,
            ActionType.EXPORT_IMAGE,
            ActionType.MORE_VIP_PRIVILEGES,
        ],
    },
];

// title bar 文件主菜单
const MAIN_FILE_MENU = {
    key: MenuKey.FILE,
    items: [
        ...(horMenu ? [{
            // 顶部横向菜单， 仅在线文档显示
            key: MenuKey.OPERATION_HOR_MENU,
            type: 'horizontal',
            items: [
                MenuKey.EXPORT_HOR,
                MenuKey.PRINT_HOR,
                MenuKey.GENERATE_COPIES_HOR,
                MenuKey.OPEN_CLIENT_HOR,
            ],
        }, DIVIDER] : []),

        // 重命名， 仅云文档显示
        MenuKey.RENAME,
        // 导出为
        {
            key: ActionType.EXPORT,
            items: [
                ActionType.NATIVE_PPT,
                ActionType.EXPORT_PDF,
                ActionType.EXPORT_IMAGE,
                ActionType.GENERATE_SHORTCUT,
            ],
        },
        // 生成副本
        ActionType.GENERATE_COPIES,
        // 另存为模板
        ActionType.SAVE_TEMP,
        MenuKey.PRINT,
        DIVIDER,

        // 本地编辑保存&另存为
        MenuKey.LOCAL_FILE_SAVE,
        MenuKey.LOCAL_FILE_SAVE_AS,
        // 本地编辑打开文件所在位置
        MenuKey.LOCAL_FILE_FOLDER,
        ...(usePrivateAuthMenu ? [DIVIDER, ActionType.PERMISSION_SETTING] : []),
        DIVIDER,

        // 保存版本
        ActionType.SAVE_REVISION,
        ActionType.REVISION_RECORD,
        ActionType.HISTORY_VERSION,
        MenuKey.FILE_INFO,
        // 私有化上传新版本,
        ActionType.ADD_REVISION,
        DIVIDER,

        // 权限设置
        {
            key: ActionType.PERMISSION_MENU,
            items: [
                ActionType.PERMISSION_SETTING,
                ActionType.ADVANCED_PERMISSION,
                ActionType.SET_WATER_MARK,
                DIVIDER,
                ActionType.TRANSFER_OWNERSHIP,
            ],
        },
        ActionType.BROWSE_RECORD,
        MenuKey.ANONYMOUS_VIEW,
        ...(exitDocument ? [ActionType.EXIT_DOCUMENT] : []),
        DIVIDER,

        // 文档个性化
        {
            key: ActionType.PERSONALIZATION,
            items: [
                // 超会设置文档高亮名
                MenuKey.HIGHLIGHT_TITLE,
            ],
        },
        showAccessibility ? ActionType.ACCESSIBILITY : FileMenuKey.UNIVERSAL_SETTING,
        ActionType.SWITCH_CERTIFICATION,
        DIVIDER,

        // 帮助与反馈
        {
            key: ActionType.HELP_FEEDBACK,
            items: [
                ActionType.HELP,
                ActionType.REPORT,
                ActionType.ACCUSATION,
                {
                    key: ActionType.PRIVACY,
                    items: [
                        ActionType.PRIVACY_POLICY,
                        ActionType.THIRD_PARTY_DATA_SHARING,
                        ActionType.THIRD_AUTHORIZATION_MANAGEMENT,
                        ActionType.PERSONAL_INFO_COLLECTION,
                    ],
                },
                ActionType.INFRINGEMENT,
                ActionType.APPEAL,
                ActionType.SHORTCUT_KEY,
                ActionType.ABOUT,
                ActionType.COMMUNITY_CONVENTION,
                ActionType.SLIDE_VERSION,
            ],
        },
        DIVIDER,
        {
            key: ActionType.DEVELOPMENT,
            items: [
                ActionType.PERFORMANCE,
            ],
        },
    ],
};

export const MENU_SCHEMA: WorkbenchMenuSchema = [
    ...COMMON_MENU_SCHEMA,
    // 插入菜单
    {
        key: ActionType.INSERT_ALL,
        items: INSERT_LIST,
    },
    {
        key: wrapIdForBig(ActionType.INSERT_ALL),
        items: INSERT_LIST,
    },
    // 快捷美化
    {
        key: ActionType.QUICK_TOOL,
        items: [
            ActionType.COLOR_HANDLER,
            ActionType.CHANGE_THEME,
            DIVIDER,
            ActionType.FONT_HANDLER,
            ActionType.UNIFORM_FONT,
            // ActionType.REPLACE_FONT,
            DIVIDER,
            ActionType.PIC_HANDLER,
            ActionType.MUTI_PICTURE_SIZE_SETTING,
            ActionType.MUTI_PICTURE_POS_SETTING,
            ActionType.MUTI_PICTURE_EXPORT,
            ActionType.DIGITAL_CONTENT_DESENSITIZATION,
            ActionType.IMAGES_JOINT,
            // ActionType.VIP_LINK_TEXT,
        ],
    },
    {
        key: ActionType.INTELLIGENT_TOOL,
        items: INTELLGENT_TOOL_LIST,
    },
    MAIN_FILE_MENU,
    PDF_TRANSFORM_MENU,
    // 工具栏单栏菜单
    {
        key: ActionType.SINGLE_TOOLBAR_MENU,
        items: SINGLE_TOOLBAR_MENU_LIST,
    },
    // 图片处理
    {
        key: ActionType.PIC_HANDLE_TOOLBAR,
        items: [
            ActionType.MUTI_PICTURE_SIZE_SETTING,
            ActionType.MUTI_PICTURE_POS_SETTING,
            ActionType.MUTI_PICTURE_EXPORT,
            // ActionType.VIP_LINK_TEXT,
            ActionType.IMAGES_JOINT,
        ],
    },
    {
        key: ActionType.FONT_EFFECT_TOOLBAR,
        items: [
            {
                key: ActionType.FONT_SHADOW_TOOLBAR,
                items: [
                    wrapIdForCustomContent(ActionType.FONT_SHADOW_TOOLBAR),
                ],
            },
            {
                key: ActionType.FONT_REFLECTION_TOOLBAR,
                items: [
                    wrapIdForCustomContent(ActionType.FONT_REFLECTION_TOOLBAR),
                ],
            },
            {
                key: ActionType.FONT_GLOW_TOOLBAR,
                items: [
                    wrapIdForCustomContent(ActionType.FONT_GLOW_TOOLBAR),
                ],
            },
        ],
    },
    {
        key: ActionType.MERGE_CELL_TOOLBAR,
        items: [
            ActionType.MERGE_CELL_TOOLBAR_TEXT,
            ActionType.MERGE_CELL_AND_SELECT,
        ],
    },
    {
        key: ActionType.CANCEL_CELL_TOOLBAR,
        items: [
            ActionType.CANCEL_CELL_TOOLBAR_TEXT,
        ],
    },
    {
        key: ActionType.TABLE_FONT_MARGIN,
        items: [
            ActionType.TABLE_FONT_MARGIN_NORMAL,
            ActionType.TABLE_FONT_MARGIN_BIG,
            ActionType.TABLE_FONT_MARGIN_SMALL,
            ActionType.TABLE_FONT_MARGIN_NONE,
        ],
    },
    {
        key: ActionType.TABLE_FONT_DIRECTION,
        items: [
            TextDirectionType.HORIZONTAL,
            TextDirectionType.VERTICAL,
            TextDirectionType.ROTATE_90,
            TextDirectionType.ROTATE_270,
            TextDirectionType.WORD_ART_VERTICAL_GENERAL,
        ],
    },
    {
        key: ActionType.DELETE_TABLE_TOOLBAR,
        items: [
            ActionType.DELETE_ROW,
            ActionType.DELETE_COL,
            DIVIDER,
            ActionType.DELETE_TABLE,
        ],
    },
    REPLACE_PICTURE_MENU,
    RESET_PICTURE_MENU,
    CROP_TO_SHAPE,
    ROTATE,
    {
        key: ActionType.TRANSITION_EFFECT_TOOLBAR,
        items: [],
    },
    {
        key: ActionType.TRANSITION_EFFECT_FADE_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_FADE_SMOOTH,
            ActionType.TRANSITION_EFFECT_FADE_BLACK,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_PUSH_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_PUSH_TOP,
            ActionType.TRANSITION_EFFECT_PUSH_RIGHT,
            ActionType.TRANSITION_EFFECT_PUSH_LEFT,
            ActionType.TRANSITION_EFFECT_PUSH_BOTTOM,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_SWITCH_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_SWITCH_RIGHT,
            ActionType.TRANSITION_EFFECT_SWITCH_LEFT,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_CUT_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_CUT_NONE,
            ActionType.TRANSITION_EFFECT_CUT_BLACK,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_PULL_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_PULL_RIGHT,
            ActionType.TRANSITION_EFFECT_PULL_BOTTOM,
            ActionType.TRANSITION_EFFECT_PULL_LEFT,
            ActionType.TRANSITION_EFFECT_PULL_TOP,
            ActionType.TRANSITION_EFFECT_PULL_LEFT_DOWN,
            ActionType.TRANSITION_EFFECT_PULL_LEFT_UP,
            ActionType.TRANSITION_EFFECT_PULL_RIGHT_DOWN,
            ActionType.TRANSITION_EFFECT_PULL_RIGHT_UP,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_COVER_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_COVER_LEFT,
            ActionType.TRANSITION_EFFECT_COVER_BOTTOM,
            ActionType.TRANSITION_EFFECT_COVER_RIGHT,
            ActionType.TRANSITION_EFFECT_COVER_TOP,
            ActionType.TRANSITION_EFFECT_COVER_LEFT_DOWN,
            ActionType.TRANSITION_EFFECT_COVER_LEFT_UP,
            ActionType.TRANSITION_EFFECT_COVER_RIGHT_DOWN,
            ActionType.TRANSITION_EFFECT_COVER_RIGHT_UP,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_WARP_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_WARP_ENLARGE,
            ActionType.TRANSITION_EFFECT_WARP_NARROW,
            ActionType.TRANSITION_EFFECT_NEWSFLASH,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_FLIP_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_FLIP_LEFT,
            ActionType.TRANSITION_EFFECT_FLIP_RIGHT,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_GALLERY_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_GALLERY_LEFT,
            ActionType.TRANSITION_EFFECT_GALLERY_RIGHT,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_PRISM_MENU,
        items: [
            ActionType.TRANSITION_EFFECT_PRISM_LEFT,
            ActionType.TRANSITION_EFFECT_PRISM_TOP,
            ActionType.TRANSITION_EFFECT_PRISM_RIGHT,
            ActionType.TRANSITION_EFFECT_PRISM_BOTTOM,
        ],
    },
    {
        key: ActionType.TRANSITION_EFFECT_EMPTY_MENU,
        items: [],
    },
    {
        key: ActionType.TRANSITION_EFFECT_FLASH_MENU,
        items: [],
    },
    {
        key: ActionType.TRANSITION_EFFECT_SIMPLE_MENU,
        items: [
            {
                key: ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE,
                items: [
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_EMPTY,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_FADE,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_PUSH,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_SWITCH,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_CUT,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_PULL,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_COVER,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_WARP,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_FLIP,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_GALLERY,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_PRISM,
                    ActionType.TRANSITION_SIMPLE_MENU_ITEM_CHANGE_FLASH,
                ],
            },
            ActionType.TRANSITION_SIMPLE_MENU_ITEM_EFFECT,
            ActionType.TRANSITION_SIMPLE_MENU_ITEM_DURATION,
            ActionType.TRANSITION_SIMPLE_MENU_ITEM_APPLY_ALL,
        ],
    },
    // 删除批注
    {
        key: ActionType.DELETE_COMMENT,
        items: [
            ActionType.DELETE_COMMENT_GROUP,
            DIVIDER,
            ActionType.DELETE_PAGE_COMMENT,
            ActionType.DELETE_ALL_COMMENT,
        ],
    },
];
